/**
 * AuthContext — yan-vada PWA authentication & access tier state.
 *
 * API-backed auth state. Cloudflare Pages Functions hold the Supabase session in
 * HttpOnly cookies; the browser only receives safe member state from /api/auth/me.
 *
 *   - Tier detection: @dgtl.lk emails = TIER 0 (core team, bypass all gates)
 *   - T&C acceptance flag (persisted across visits)
 *   - Sponsor-specific NDA acceptance cache (server remains source of truth)
 *   - Sign-in start actions (Google OAuth + approved email magic links)
 *   - Gate-dismissal actions backed by legal endpoints
 *
 * Access tiers:
 *   TIER_0  Core team (@dgtl.lk)     → full access, no gates
 *   TIER_1  Signed-in external        → pitch + account, passed gates
 *   TIER_2  Public / not signed in    → hero only + legal pages
 *   TIER_3  Interns (TBD)           → likely TIER_0 after NDA-TM
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { clearYanVadaBrowserState } from '../lib/fullSignOut'

export const CORE_TEAM_DOMAIN = 'dgtl.lk'

const KEYS = {
  EMAIL: 'yan_email',
  MACHINE_KNOWN: 'yan_machine_known',
  TC_ACCEPTED: 'yan_tc_accepted',
  NDA_ACCEPTED: 'yan_nda_accepted',
  NDA_ACCEPTED_SPONSORS: 'yan_nda_accepted_sponsors',
} as const

export type AccessTier = 0 | 1 | 2 | 3

interface AuthState {
  email: string | null
  knownMachine: boolean
  isLoggedIn: boolean
  tcAccepted: boolean
  ndaAccepted: boolean
  acceptedNdaSponsors: string[]
  tier: AccessTier
  role: string | null
  status: string | null
  displayName: string | null
  firstName: string | null
  lastName: string | null
  profileImageUrl: string | null
  profileType: string | null
  rights: Record<string, boolean>
  questionnaireUrl: string | null
  profileConfirmedAt: string | null
  loading: boolean
}

interface AuthActions {
  acceptTC: () => void
  acceptNDA: (sponsorSlug?: string) => Promise<void>
  signInWithGoogle: (next?: string) => Promise<void>
  signInWithApple: (next?: string) => Promise<void>
  requestMagicLink: (email: string, next?: string) => Promise<{ status?: string; message?: string; error?: string; detail?: string }>
  refreshAuth: () => Promise<void>
  signOut: () => Promise<void>
}

type AuthValue = AuthState & AuthActions

const DEFAULT_STATE: AuthState = {
  email: null,
  knownMachine: false,
  isLoggedIn: false,
  tcAccepted: false,
  ndaAccepted: false,
  acceptedNdaSponsors: [],
  tier: 2,
  role: null,
  status: null,
  displayName: null,
  firstName: null,
  lastName: null,
  profileImageUrl: null,
  profileType: null,
  rights: {},
  questionnaireUrl: null,
  profileConfirmedAt: null,
  loading: true,
}

function deriveTier(email: string | null, tcAccepted: boolean, ndaAccepted: boolean): AccessTier {
  const normalized = email?.toLowerCase() ?? null
  if (normalized && normalized.endsWith(`@${CORE_TEAM_DOMAIN}`)) return 0
  if (!tcAccepted) return 2
  if (!normalized) return 2
  if (!ndaAccepted) return 1
  return 1
}

const AuthContext = createContext<AuthValue | null>(null)

function normalizeSponsorSlug(sponsorSlug = 'master') {
  return sponsorSlug.trim().toLowerCase() || 'master'
}

function safeNextPath(next = '/') {
  return next.startsWith('/') && !next.startsWith('//') ? next : '/'
}

function readAcceptedNdaSponsors() {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEYS.NDA_ACCEPTED_SPONSORS) ?? '[]')
    if (!Array.isArray(parsed)) return []
    return parsed.filter((slug): slug is string => typeof slug === 'string' && slug.length > 0)
  } catch {
    return []
  }
}

function writeAcceptedNdaSponsors(slugs: string[]) {
  localStorage.setItem(KEYS.NDA_ACCEPTED_SPONSORS, JSON.stringify(slugs))
  localStorage.removeItem(KEYS.NDA_ACCEPTED)
}

function readStored(): Partial<AuthState> {
  try {
    const email = localStorage.getItem(KEYS.EMAIL)
    const tcAccepted = localStorage.getItem(KEYS.TC_ACCEPTED) === 'true'
    const acceptedNdaSponsors = readAcceptedNdaSponsors()
    const knownMachine = localStorage.getItem(KEYS.MACHINE_KNOWN) === 'true' || Boolean(email)
    return { email, knownMachine, tcAccepted, ndaAccepted: acceptedNdaSponsors.length > 0, acceptedNdaSponsors }
  } catch {
    return {}
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const stored = readStored()
    const tcAccepted = stored.tcAccepted ?? false
    const ndaAccepted = stored.ndaAccepted ?? false
    const acceptedNdaSponsors = stored.acceptedNdaSponsors ?? []
    return {
      email: null,
      knownMachine: stored.knownMachine ?? false,
      isLoggedIn: false,
      tcAccepted,
      ndaAccepted,
      acceptedNdaSponsors,
      tier: deriveTier(null, tcAccepted, ndaAccepted),
      role: null,
      status: null,
      displayName: null,
      firstName: null,
      lastName: null,
      profileImageUrl: null,
      profileType: null,
      rights: {},
      questionnaireUrl: null,
      profileConfirmedAt: null,
      loading: true,
    }
  })

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' })
      const contentType = res.headers.get('content-type') ?? ''
      if (!contentType.includes('json')) throw new Error('auth_status_unavailable')
      const data = await res.json() as {
        isLoggedIn?: boolean
        email?: string
        member?: {
          role?: string
          status?: string
          displayName?: string | null
          firstName?: string | null
          lastName?: string | null
          profileImageUrl?: string | null
          profileType?: string | null
          rights?: Record<string, boolean>
          questionnaireUrl?: string | null
          profileConfirmedAt?: string | null
        }
      }
      if (!res.ok) throw new Error('auth_status_unavailable')
      setState(prev => {
        const email = data.isLoggedIn ? (data.email ?? null) : null
        const knownMachine = prev.knownMachine || !!email
        try {
          if (email) {
            localStorage.setItem(KEYS.EMAIL, email)
            localStorage.setItem(KEYS.MACHINE_KNOWN, 'true')
          }
        } catch { /* noop */ }
        return {
          ...prev,
          email,
          knownMachine,
          isLoggedIn: !!email,
          role: data.member?.role ?? null,
          status: data.member?.status ?? null,
          displayName: data.member?.displayName ?? null,
          firstName: data.member?.firstName ?? null,
          lastName: data.member?.lastName ?? null,
          profileImageUrl: data.member?.profileImageUrl ?? null,
          profileType: data.member?.profileType ?? null,
          rights: data.member?.rights ?? {},
          questionnaireUrl: data.member?.questionnaireUrl ?? null,
          profileConfirmedAt: data.member?.profileConfirmedAt ?? null,
          tier: deriveTier(email, prev.tcAccepted, prev.ndaAccepted),
          loading: false,
        }
      })
    } catch {
      setState(prev => ({ ...prev, loading: false }))
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const acceptTC = useCallback(() => {
    setState(prev => {
      const next = { ...prev, tcAccepted: true, tier: deriveTier(prev.email, true, prev.ndaAccepted) }
      try { localStorage.setItem(KEYS.TC_ACCEPTED, 'true') } catch { /* noop */ }
      return next
    })
    if (state.isLoggedIn) {
      void fetch('/api/legal/accept-tc', { method: 'POST', credentials: 'include' })
    }
  }, [state.isLoggedIn])

  const acceptNDA = useCallback(async (sponsorSlug = 'master') => {
    const normalizedSlug = normalizeSponsorSlug(sponsorSlug)
    const res = await fetch('/api/legal/accept-nda', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ sponsorSlug: normalizedSlug }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({})) as { error?: string }
      throw new Error(data.error ?? 'Unable to record NDA acceptance')
    }
    setState(prev => {
      const acceptedNdaSponsors = Array.from(new Set([...prev.acceptedNdaSponsors, normalizedSlug]))
      const next = { ...prev, ndaAccepted: true, acceptedNdaSponsors, tier: 1 as AccessTier }
      try { writeAcceptedNdaSponsors(acceptedNdaSponsors) } catch { /* noop */ }
      return next
    })
  }, [])

  const signInWithGoogle = useCallback(async (next = '/') => {
    window.location.href = `/api/auth/start?provider=google&next=${encodeURIComponent(safeNextPath(next))}`
  }, [])

  const signInWithApple = useCallback(async (next = '/') => {
    window.location.href = `/api/auth/start?provider=apple&next=${encodeURIComponent(safeNextPath(next))}`
  }, [])

  const requestMagicLink = useCallback(async (email: string, next = '/') => {
    const res = await fetch('/api/auth/start', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ provider: 'email', email, next: safeNextPath(next), legacyEmailFallback: true }),
    })
    const contentType = res.headers.get('content-type') ?? ''
    if (!contentType.includes('json')) {
      return { error: 'Auth endpoint did not return JSON. Check Cloudflare API challenge/routing.' }
    }
    const data = await res.json() as { status?: string; message?: string; error?: string; detail?: string }
    if (!res.ok && !data.error) return { ...data, error: 'Unable to request sign-in link' }
    return data
  }, [])

  const signOut = useCallback(async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => undefined)
    await clearYanVadaBrowserState()
    setState(prev => ({
      ...DEFAULT_STATE,
      knownMachine: prev.knownMachine,
      tcAccepted: prev.tcAccepted,
      tier: deriveTier(null, prev.tcAccepted, false),
      loading: false,
    }))
  }, [])

  const value: AuthValue = { ...state, acceptTC, acceptNDA, signInWithGoogle, signInWithApple, requestMagicLink, refreshAuth: refresh, signOut }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
